import { toLevel } from '@studio/design-system/messaging';
import { generateId } from '@studio/models';
import { isNotNil } from '@tryiris-ai/js-types';
import type { CloseReason, Fragment, LifecycleEvent, Notification, Options } from './types.js';
import { depot } from '#~/depot/index.js';
import { StudioError } from '#~/error/index.js';
import { logger } from '#~/logger/index.js';

/**
 * Clear a notification from the store.
 *
 * @param notificationId - ID of the notification to be cleared.
 */
export const clear = (notificationId: Notification['id']): void => {
  const { produce } = depot.getState();

  if (pendingNotifications.has(notificationId)) {
    clearTimeout(pendingNotifications.get(notificationId));
    pendingNotifications.delete(notificationId);
  }

  produce((draft) => {
    delete draft.notifications[notificationId];
  });
};

/** Clear all notifications from the store, including delayed posts still pending. */
export const clearAll = (): void => {
  const { produce } = depot.getState();

  // `pendingNotifications` is a Map: `Object.keys` on it is always empty, so
  // delayed posts used to survive clearAll (ENG-4930 item 7).
  pendingNotifications.forEach((timeout) => {
    clearTimeout(timeout);
  });
  pendingNotifications.clear();

  produce((draft) => {
    draft.notifications = {};
  });
};

/**
 * Indicates if a notification with the specified ID is already posted.
 *
 * @param notificationId - ID of the notification to be checked.
 * @returns `true` if the notification is posted, `false` otherwise.
 */
export const has = (notificationId: Notification['id']): boolean => {
  const { notifications } = depot.getState();

  return isNotNil(notifications[notificationId]);
};

/**
 * Hide a notification from the store.
 *
 * @param notificationId - ID of the notification to be cleared.
 */
export const hide = (notificationId: Notification['id']): void => {
  const { produce } = depot.getState();

  if (pendingNotifications.has(notificationId)) {
    clearTimeout(pendingNotifications.get(notificationId));
    pendingNotifications.delete(notificationId);
  }

  produce((draft) => {
    const notification = draft.notifications[notificationId];

    if (isNotNil(notification)) {
      notification[2].isClosed = true;
    }
  });
};

const closingNotifications = new Set<Notification['id']>();

/** Whether a close is committing its store change and lifecycle event. */
export const isClosing = (id: Notification['id']): boolean => closingNotifications.has(id);

const lifecycleListeners = new Set<(event: LifecycleEvent) => void>();

const emitLifecycle = (event: LifecycleEvent): void => {
  for (const listener of lifecycleListeners) listener(event);
};

/**
 * Listen for notices closing (with why) and for their action buttons being
 * pressed. Sits beside the panel's `hide`, never in place of it (ENG-4930 item 2).
 *
 * @param listener - Called with each lifecycle event.
 * @returns Unsubscribe function.
 */
export const subscribeNotificationLifecycle = (
  listener: (event: LifecycleEvent) => void
): (() => void) => {
  lifecycleListeners.add(listener);

  return () => {
    lifecycleListeners.delete(listener);
  };
};

/**
 * Close a notification the way the panel does (`hide`), and tell lifecycle
 * listeners why: `dismiss` for the close X, `timeout` for auto-close.
 *
 * @param notificationId - ID of the notification being closed.
 * @param reason - Why it closed.
 */
export const close = (
  notificationId: Notification['id'],
  reason: Extract<CloseReason, 'dismiss' | 'timeout'>
): void => {
  // Store subscribers run synchronously inside hide. Keep this transaction
  // identifiable until its reason is emitted, including reentrant close calls.
  if (isClosing(notificationId)) return;
  closingNotifications.add(notificationId);
  try {
    hide(notificationId);
    emitLifecycle({ type: 'closed', id: notificationId, reason });
  } finally {
    closingNotifications.delete(notificationId);
  }
};

/**
 * Report that one of a notification's action buttons was pressed.
 *
 * @param notificationId - ID of the notification acted on.
 * @param action - Analytics name of the action pressed.
 */
export const acted = (notificationId: Notification['id'], action: string): void => {
  emitLifecycle({ type: 'acted', id: notificationId, action });
};

/** Mark all notifications as read. */
export const markAllRead = (): void => {
  const { produce } = depot.getState();

  produce((draft) => {
    Object.values(draft.notifications).forEach((notification) => {
      notification[2].isRead = true;
    });
  });
};

/**
 * Mark a notification as read.
 *
 * @param notificationId - ID of the notification to be marked.
 */
export const markRead = (notificationId: Notification['id']): void => {
  const { produce } = depot.getState();

  produce((draft) => {
    const notification = draft.notifications[notificationId];

    if (isNotNil(notification)) {
      notification[2].isRead = true;
    }
  });
};

const pendingNotifications = new Map<Notification['id'], NodeJS.Timeout>();

/**
 * Whether a notice is queued but has not reached the store yet.
 *
 * @param notificationId - ID of the notification to be checked.
 * @returns Whether its delivery timer is still pending.
 */
export const isPending = (notificationId: Notification['id']): boolean =>
  pendingNotifications.has(notificationId);

/**
 * Post a notification to the store.
 *
 * @param notification - Notification to be posted.
 * @param options - Posting options.
 * @param onPosted - Called after delivery to the store, never if cancelled.
 * @returns ID of the notification that was posted.
 */
export const post = (
  notification: Fragment,
  options?: Options,
  onPosted?: () => void
): Notification['id'] => {
  const { produce } = depot.getState();
  const { message, timestamp } = notification;
  let { id } = notification;

  // If we haven't explicitly been given an ID, see if we have a duplicate.
  if (!id) {
    const match = Object.values(depot.getState().notifications).find(
      (µ) => µ[0].message === message
    );

    // If we have a duplicate, use its ID. Otherwise, generate a random one.
    id = match ? match[0].id : generateId();
  }

  const n = {
    ...notification,
    id,
    message,
    timestamp: timestamp ?? new Date()
  };

  if (pendingNotifications.has(n.id)) {
    clearTimeout(pendingNotifications.get(n.id));
    pendingNotifications.delete(n.id);
  }

  pendingNotifications.set(
    n.id,
    setTimeout(() => {
      pendingNotifications.delete(n.id);

      produce((draft) => {
        // Keep callers' readonly action lists separate from Immer's mutable draft.
        draft.notifications[n.id] = [
          n,
          options && { ...options, actions: options.actions?.slice() },
          { isClosed: false, isRead: false }
        ];
      });
      onPosted?.();
    }, options?.delay ?? 0)
  );

  return id;
};

/**
 * Raise a notification for the specified error.
 *
 * @param error - Error to be raised.
 * @param [options] - Notification options.
 * @returns ID of the notification that was raised.
 */
export const raiseError = (
  error: Error | string,
  options?: Options & Pick<Notification, 'severity' | 'title' | 'actionable'>
): Notification['id'] => {
  // Default false (boolean, never explicit undefined) so omitted callers stay
  // non-actionable and metadata cannot leak into tuple[1] Options.
  const { severity, title, actionable = false, ...rest } = options ?? {};

  const notification = {
    actionable,
    id: error instanceof StudioError ? error.id : undefined,
    message: error instanceof Error ? error.message : error,
    severity: severity ?? (error instanceof StudioError ? error.severity : 'error'),
    title: title ?? (error instanceof StudioError ? error.title : 'Error')
  };

  // Log message to the console.
  logger.log(toLevel(notification.severity), error);

  return post(notification, rest);
};

// Bug 3 (spec §3.3): the Multi-Add per-batch notification entity APIs
// (`postMultiAddBatch` / `dismissMultiAddBatch` / `updateMultiAddBatch` /
// `getActiveMultiAddBatches` / `isUnresolved` and the
// `multiAddBatchNotifications` depot slice they wrote) were removed with
// the pinned bell-batch card. The general `post()` toast above is now the
// single Multi-Add surface; the bell badge is a primitive unread count.
